import { Button, Col, Divider, Form, FormInstance, Row, Space, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FC, KeyboardEvent, MouseEvent, useMemo, useState } from 'react';
import { XmModal } from 'src/component/modal/XmModal';
import { XmTableScope } from 'src/component/table/XmTable';
import useReadonly from 'src/hook/useReadonly';
import { clearedFields } from './recurrenceFields';
import { toResultValues } from './recurrenceValues';
import { RecurrenceOccurrence, RecurrenceResultValues, RecurrenceValues } from './types';
import useRecurrenceRule from './useRecurrenceRule';
import XmRecurrenceFields from './XmRecurrenceFields';

const LAYOUT = {
  labelCol: { span: 11 },
  wrapperCol: { span: 13 },
} as const;

// ch, not % — antd measures this width in an unconstrained hidden cell, where % resolves to 0
const TITLE_STYLE = { display: 'inline-block', maxWidth: '44ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

interface Props {
  readonly open: boolean;
  readonly form: FormInstance<RecurrenceValues>;
  readonly onSubmit: (values: RecurrenceResultValues) => void;
  readonly onCancel: () => void;
}

const XmRecurrenceModal: FC<Props> = ({ open, form, onSubmit, onCancel }) => {
  const values: RecurrenceValues = Form.useWatch([], form);
  const { ruleText, occurrences } = useRecurrenceRule(values);
  const [tscope] = useState(() => new XmTableScope<RecurrenceOccurrence>('plain'));
  const { isDisabled } = useReadonly();

  const columns: ColumnsType<RecurrenceOccurrence> = useMemo(
    () => [
      {
        title: <Tooltip title={ruleText}><span style={TITLE_STYLE}>Dates: {ruleText}</span></Tooltip>,
        dataIndex: 'date',
      },
    ],
    [ruleText]
  );

  const toResult = (): RecurrenceResultValues => toResultValues(form.getFieldsValue());

  const handleValuesChange = (): void => {
    const cleared = clearedFields(form.getFieldsValue());
    if (Object.keys(cleared).length) form.setFieldsValue(cleared);
  };

  // clicking the mask itself must not cancel the dialog
  const handleMaskCancel = (e: MouseEvent<Element> | KeyboardEvent<HTMLElement>): void => {
    if (!(e.target as Element).classList?.contains('ant-modal-wrap')) onCancel();
  };

  return (
    <XmModal
      title='Recurrence rule'
      open={open}
      onOk={() => form.submit()}
      onCancel={handleMaskCancel}
      footer={null}
      width={800}
    >
      <Row style={{ marginBottom: '10px' }} gutter={[24, 8]}>
        <Col span={12}>
          <Divider titlePlacement='left' plain style={{ marginTop: 10 }}>
            Definition
          </Divider>
          <Form
            form={form}
            onFinish={() => onSubmit(toResult())}
            onValuesChange={handleValuesChange}
            autoComplete='off'
            {...LAYOUT}
          >
            <XmRecurrenceFields form={form} values={values} />
            <Form.Item style={{ float: 'right' }}>
              <Space>
                <Button htmlType='button' onClick={onCancel}>
                  Cancel
                </Button>
                <Button htmlType='submit' type='primary' disabled={isDisabled}>
                  Use this rule
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Col>
        <Col span={12}>
          <Divider titlePlacement='left' plain style={{ marginTop: 10 }}>
            Nearest future occurrences
          </Divider>
          {values?.freq && (
            <tscope.XmTable
              pagination={false}
              columns={columns}
              dataSource={occurrences}
              scroll={{ y: 365 }}
            />
          )}
        </Col>
      </Row>
    </XmModal>
  );
};

export default XmRecurrenceModal;
